import { Slider as RadixSlider } from "radix-ui";
import { cn } from "../lib/cn";

export interface SliderProps {
	value?: number;
	defaultValue?: number;
	onValueChange?: (value: number) => void;
	onValueCommit?: (value: number) => void;
	min?: number;
	max?: number;
	step?: number;
	disabled?: boolean;
	label?: string;
	/** Читаемое значение для скринридера: «124.80 доллара», а не «62». */
	valueText?: string;
	className?: string;
}

/**
 * Дорожка 4px, шайба 20px. Один палец — в системе нет диапазонов: доля
 * погашения Баллами задаётся одним числом от нуля до потолка.
 *
 * Radix ставит Thumb абсолютно от padding-box корня и сам держит его
 * в границах: на 0 % и 100 % шайба flush с краем Root. Паддинг на Root
 * сдвигает только дорожку, шайбу — нет. Поэтому inset (`px-2.5` = половина
 * size-5) снаружи: предок с `overflow-x: hidden` (`.iec-scroll` в Dialog)
 * больше не срезает кружок и тень `shadow-knob`. Overflow у корня не hidden.
 * Thumb живёт вне Track, overflow-hidden дорожки шайбу не ест.
 * Ховер затемняет заливку; на таче шайбу читают по усиленной тени, не по ховеру.
 */
export function Slider({
	value,
	defaultValue = 0,
	onValueChange,
	onValueCommit,
	min = 0,
	max = 100,
	step = 1,
	disabled,
	label,
	valueText,
	className,
}: SliderProps) {
	return (
		<div className="w-full px-2.5">
			<RadixSlider.Root
				value={value !== undefined ? [value] : undefined}
				defaultValue={[defaultValue]}
				onValueChange={([next]) => next !== undefined && onValueChange?.(next)}
				onValueCommit={([next]) => next !== undefined && onValueCommit?.(next)}
				min={min}
				max={max}
				step={step}
				disabled={disabled}
				className={cn(
					"relative flex h-6 w-full touch-none select-none items-center",
					disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
					className,
				)}
			>
				<RadixSlider.Track className="relative h-1 w-full grow overflow-hidden rounded-full bg-sunken">
					<RadixSlider.Range className="absolute h-full rounded-full bg-brand" />
				</RadixSlider.Track>
				<RadixSlider.Thumb
					aria-label={label}
					aria-valuetext={valueText}
					className={cn(
						"block size-5 cursor-pointer rounded-full bg-card shadow-knob",
						"transition-colors duration-150 ease-smooth",
						"hover:bg-sunken",
						"focus-ring",
						"data-disabled:cursor-not-allowed data-disabled:hover:bg-card",
					)}
				/>
			</RadixSlider.Root>
		</div>
	);
}
